import RoutePageTitle from "components/RoutePageTitle";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

// MUI components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import CircularProgress from "@mui/material/CircularProgress";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import * as XLSX from "xlsx";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DataTable from "examples/Tables/DataTable";

// Layout components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useTranslation } from "react-i18next";

// Context and utils
import { useUserContext } from "context/UserContext";
import getGraphQLEndpoint from "utils/getGraphQLEndpoint";

const VENUE_LIST_PATH = "/api/v1/venue/listv2";
const VENUE_CREATE_PATH = "/api/v1/venue/createv2";
const VENUE_UPDATE_PATH = "/api/v1/venue/update_venue";
const MACHINE_VENUE_UPDATE_PATH = "/api/v1/venue/update";
const API_KEY = "57b2a9ba-4428-48e4-b137-f8337b68595e";

const outlineButtonSx = {
  borderColor: "#1976d2",
  color: "#1976d2",
  fontWeight: 600,
  "&:hover": {
    borderColor: "#115293",
    backgroundColor: "rgba(25, 118, 210, 0.08)",
  },
  "&.Mui-disabled": {
    color: "rgba(25, 118, 210, 0.45)",
    borderColor: "rgba(25, 118, 210, 0.3)",
  },
};
const headerButtonSx = {
  borderColor: "#ffffff",
  color: "#ffffff",
  fontWeight: 600,
  "&:hover": {
    borderColor: "#ffffff",
    backgroundColor: "rgba(255, 255, 255, 0.15)",
  },
  "&.Mui-disabled": {
    color: "rgba(255, 255, 255, 0.65)",
    borderColor: "rgba(255, 255, 255, 0.4)",
  },
};

const selectSx = {
  "& .MuiOutlinedInput-root": {
    minHeight: 40,
    height: 40,
  },
  "& .MuiSelect-select": {
    minHeight: 40,
    height: 40,
    display: "flex",
    alignItems: "center",
    paddingTop: "0px",
    paddingBottom: "0px",
  },
};

const trimTrailingSlash = (value) => value.replace(/\/+$/, "");
const stripGraphQLSuffix = (value) => value.replace(/\/GraphQLHandler$/, "");
const buildBackendBase = (graphqlEndpoint) =>
  trimTrailingSlash(stripGraphQLSuffix(graphqlEndpoint || ""));
const resolveBackendBase = (graphqlEndpoint) => {
  const base = buildBackendBase(graphqlEndpoint);
  const fallback = typeof window !== "undefined" ? window.location.origin : "";
  if (!base || base === "undefined" || base === "null") return fallback;
  try {
    new URL(base);
    return base;
  } catch (error) {
    return fallback || base;
  }
};

const buildApiUrl = (base, path, params = {}) => {
  const url = new URL(path, base);
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    url.searchParams.set(key, value);
  });
  return url.toString();
};

const pad2 = (value) => String(value).padStart(2, "0");
const formatDateInputValue = (value) => {
  if (!value) return "";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const year = date.getFullYear();
  const month = pad2(date.getMonth() + 1);
  const day = pad2(date.getDate());
  return `${year}-${month}-${day}`;
};

const formatDateToRFC3339 = (dateValue, endOfDay = false) => {
  if (!dateValue) return "";
  if (typeof dateValue === "string" && dateValue.includes("T")) {
    return dateValue;
  }
  if (typeof dateValue === "string" && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
    const [year, month, day] = dateValue.split("-").map(Number);
    const hours = endOfDay ? 23 : 0;
    const minutes = endOfDay ? 59 : 0;
    const seconds = endOfDay ? 59 : 0;
    const date = new Date(year, month - 1, day, hours, minutes, seconds);
    const offsetMinutes = -date.getTimezoneOffset();
    const sign = offsetMinutes >= 0 ? "+" : "-";
    const absMinutes = Math.abs(offsetMinutes);
    const offsetHours = pad2(Math.floor(absMinutes / 60));
    const offsetMins = pad2(absMinutes % 60);
    return `${year}-${pad2(month)}-${pad2(day)}T${pad2(hours)}:${pad2(minutes)}:${pad2(
      seconds
    )}${sign}${offsetHours}:${offsetMins}`;
  }
  return String(dateValue);
};

const fetchJson = async (url, options = {}) => {
  const res = await fetch(url, options);
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    const extra = text ? ` ${text}` : "";
    throw new Error(`Request failed (${res.status})${extra}`);
  }
  return res.json();
};

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const buildBrandOptionsFromVenues = (list = []) => {
  const brandMap = new Map();

  normalizeArray(list).forEach((item) => {
    const brand = item?.brand || {};
    const value = String(brand.id || "").trim();
    const label = String(brand.name || brand.ref || value).trim();
    if (!value || !label || brandMap.has(value)) return;
    brandMap.set(value, { value, label });
  });

  return Array.from(brandMap.values()).sort((a, b) => a.label.localeCompare(b.label));
};

const formatValue = (value, yesLabel, noLabel) => {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "boolean") return value ? yesLabel : noLabel;
  if (Array.isArray(value)) return value.join(", ");
  return String(value);
};

const normalizeArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.list)) return value.list;
  return [];
};

const extractMachines = (item) => {
  if (!item || typeof item !== "object") return [];
  const candidates = [
    item.machines,
    item.machine,
    item.machine_list,
    item.machines_list,
    item.venue?.machines,
    item.venue?.machine,
    item.venue?.machine_list,
    item.venue?.machines_list,
    item.devices,
    item.venue?.devices,
  ];
  for (let i = 0; i < candidates.length; i += 1) {
    const normalized = normalizeArray(candidates[i]);
    if (normalized.length) return normalized;
  }
  return [];
};

const firstFilled = (...values) => {
  const found = values.find((value) => value !== null && value !== undefined && value !== "");
  return found === undefined ? "" : found;
};

const normalizeMachineVenueList = (items = []) =>
  normalizeArray(items).flatMap((item) => {
    const venue = item?.venue || {};
    const brand = item?.brand || {};
    return extractMachines(item).map((machine) => ({
      ...machine,
      id: firstFilled(machine.id, machine.ref),
      device_id: firstFilled(
        machine.array_options?.options_device_id,
        machine.device_id,
        machine.name,
        machine.ref
      ),
      display_name: firstFilled(
        machine.display_name,
        machine.machine_code,
        machine.machine_name,
        machine.name,
        machine.ref
      ),
      current_venue_id: venue.id || machine.array_options?.options_venue_id || "",
      current_venue_name: venue.name || "",
      current_city: venue.city || "",
      current_brand_name: brand.name || "",
      current_updated_at: venue.updated_at || "",
      is_online: machine.is_online ?? true,
      can_operate: machine.can_operate ?? String(machine.status || "1") === "1",
    }));
  });

const getMachineUpdateId = (machine) =>
  firstFilled(
    machine?.id,
    machine?.third_party_id,
    machine?.thirdPartyId,
    machine?.dolibarr_id,
    machine?.dolibarrId,
    machine?.machine_id,
    machine?.machineId,
    machine?.device_id
  );

const ActionButtonsCell = ({
  row,
  onView,
  onUpdate,
  onDisable,
  viewLabel,
  updateLabel,
  disableLabel,
  disabledLabel,
  updatingIds,
}) => {
  const payload = row.original.raw || row.original;
  const status = row.original.status ?? payload?.venue?.status ?? "";
  const isDisabled = status === "inactive";
  const isUpdating = updatingIds.has(payload?.venue?.id);
  return (
    <MDBox display="flex" gap={1}>
      <Button variant="outlined" size="small" sx={outlineButtonSx} onClick={() => onView(payload)}>
        {viewLabel}
      </Button>
      <Button
        variant="outlined"
        size="small"
        sx={outlineButtonSx}
        onClick={() => onUpdate(payload)}
      >
        {updateLabel}
      </Button>
      <Button
        variant="outlined"
        size="small"
        sx={outlineButtonSx}
        onClick={() => onDisable(payload)}
        disabled={isDisabled || isUpdating}
      >
        {isDisabled ? disabledLabel : disableLabel}
      </Button>
    </MDBox>
  );
};

ActionButtonsCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.object.isRequired,
  }).isRequired,
  onView: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onDisable: PropTypes.func.isRequired,
  viewLabel: PropTypes.string.isRequired,
  updateLabel: PropTypes.string.isRequired,
  disableLabel: PropTypes.string.isRequired,
  disabledLabel: PropTypes.string.isRequired,
  updatingIds: PropTypes.instanceOf(Set).isRequired,
};

const inOutTranslate = {
  outdoor: "室外",
  indoor: "室內",
};

const crowdLevelTranslate = {
  low: "低",
  medium: "中",
  high: "高",
};

const regionZoneTranslate = {
  north: "北部",
  central: "中部",
  south: "南部",
  east: "東部",
  overseas: "海外",
};

const statusTranslate = {
  active: "啟用中",
  inactive: "未啟用",
  closed: "關閉",
};

function VenuesBK() {
  const { t } = useTranslation("common");
  const { userData } = useUserContext() || {};
  const backendUrl = useMemo(getGraphQLEndpoint, []);
  const backendBaseUrl = useMemo(() => resolveBackendBase(backendUrl), [backendUrl]);
  const email = userData?.email ? userData.email.trim().toLowerCase() : "";

  const [venues, setVenues] = useState([]);
  const [brands, setBrands] = useState([]);
  const [activeVenueOptions, setActiveVenueOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [viewMode, setViewMode] = useState("venues");
  const [machineRows, setMachineRows] = useState([]);
  const [machineLoading, setMachineLoading] = useState(false);
  const [machineError, setMachineError] = useState("");
  const [machineVenueOpen, setMachineVenueOpen] = useState(false);
  const [machineVenueTarget, setMachineVenueTarget] = useState(null);
  const [machineVenueId, setMachineVenueId] = useState("");
  const [machineVenueSubmitting, setMachineVenueSubmitting] = useState(false);
  const [machineVenueError, setMachineVenueError] = useState("");
  const [machineVenueSuccess, setMachineVenueSuccess] = useState("");

  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedVenue, setSelectedVenue] = useState(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    brand_id: "",
    name: "",
    full_address: "",
    country_code: "TW",
    zip_code: "",
    city: "",
    district: "",
    street: "",
    floor: "",
    region_zone: "north",
    is_domestic: true,
    place_type: "",
    crowd_level: "",
    inout: "",
    // latitude: "",
    // longitude: "",
    nearby_landmarks: "",
    target_audience: "",
    venue_type: "",
    venue_start_time: "",
    venue_end_time: "",
    external_store_code: "",
    external_system_type: "",
    external_description: "",
    sales_warehouse_name: "",
  });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");
  const [createSuccess, setCreateSuccess] = useState("");
  const [updateOpen, setUpdateOpen] = useState(false);
  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);
  const [downloadFormat, setDownloadFormat] = useState("csv");
  const [updateForm, setUpdateForm] = useState({});
  const [updateOriginal, setUpdateOriginal] = useState({});
  const [updateTarget, setUpdateTarget] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [updateError, setUpdateError] = useState("");
  const [updateSuccess, setUpdateSuccess] = useState("");
  const [statusUpdatingIds, setStatusUpdatingIds] = useState(() => new Set());
  const [statusError, setStatusError] = useState("");
  const [statusSuccess, setStatusSuccess] = useState("");

  const loadVenues = useCallback(async () => {
    if (!email) {
      setError(t("venuesPage.messages.emailRequired"));
      return [];
    }
    setLoading(true);
    setError("");
    try {
      const url = buildApiUrl(backendBaseUrl, VENUE_LIST_PATH, { email, status: "all" });
      const json = await fetchJson(url);
      const payload = Array.isArray(json.data) ? json.data : [];
      setVenues(payload);
      setBrands(buildBrandOptionsFromVenues(payload));
      return payload;
    } catch (err) {
      console.error(err);
      setError(t("venuesPage.messages.loadFailed"));
      return [];
    } finally {
      setLoading(false);
    }
  }, [backendBaseUrl, email, t]);

  const loadMachines = useCallback(async () => {
    if (!email) {
      setMachineError(t("venuesPage.messages.emailRequired"));
      return [];
    }
    setMachineLoading(true);
    setMachineError("");
    try {
      const url = buildApiUrl(backendBaseUrl, VENUE_LIST_PATH, { email, status: "all" });
      const json = await fetchJson(url, {
        headers: {
          "Content-Type": "application/json",
          "Mdm-Auth": API_KEY,
        },
      });
      if (json?.code && json.code !== 200) {
        throw new Error(json.message || "載入機台資料失敗");
      }
      const payload = normalizeMachineVenueList(json?.data ?? json);
      setMachineRows(payload);
      return payload;
    } catch (err) {
      console.error(err);
      setMachineError(err.message || "載入機台資料失敗");
      setMachineRows([]);
      return [];
    } finally {
      setMachineLoading(false);
    }
  }, [backendBaseUrl, email, t]);

  const loadActiveVenueOptions = useCallback(async () => {
    if (!email) {
      setActiveVenueOptions([]);
      return [];
    }
    try {
      const url = buildApiUrl(backendBaseUrl, VENUE_LIST_PATH, { email });
      const json = await fetchJson(url);
      const payload = Array.isArray(json.data) ? json.data : [];
      const options = payload
        .map((item) => {
          const venue = item.venue || item;
          const brand = item.brand || {};
          return {
            value: String(venue.id || ""),
            label: [venue.name, venue.city, brand.name].filter(Boolean).join(" / "),
            venue,
            brand,
          };
        })
        .filter((option) => option.value && option.label)
        .sort((a, b) => a.label.localeCompare(b.label));
      setActiveVenueOptions(options);
      return options;
    } catch (err) {
      console.error(err);
      setActiveVenueOptions([]);
      return [];
    }
  }, [backendBaseUrl, email]);

  useEffect(() => {
    loadVenues();
    loadActiveVenueOptions();
  }, [loadActiveVenueOptions, loadVenues]);

  const venueOptions = useMemo(
    () =>
      venues
        .map((item) => {
          const venue = item.venue || item;
          const brand = item.brand || {};
          return {
            value: String(venue.id || ""),
            label: [venue.name, venue.city, brand.name].filter(Boolean).join(" / "),
            venue,
            brand,
          };
        })
        .filter((option) => option.value && option.label)
        .sort((a, b) => a.label.localeCompare(b.label)),
    [venues]
  );

  const venueRows = useMemo(() => {
    return venues.map((item) => {
      const venue = item.venue || {};
      const brand = item.brand || {};
      const machines = extractMachines(item);
      return {
        // id: venue.id || "",
        name: venue.name || "",
        full_address: venue.full_address || "",
        city: venue.city || "",
        brand_name: brand.name || "",
        machine_count: venue.machine_count ?? machines.length,
        updated_at: venue.updated_at ? venue.updated_at.split(".")[0].replace("T", " ") : "",
        status: venue.status ?? "",
        raw: { venue, brand, machines },
      };
    });
  }, [venues]);

  const handleOpenDetail = useCallback((payload) => {
    const status = payload?.venue?.status;
    const crowdLevel = payload?.venue?.crowd_level;
    const regionZone = payload?.venue?.region_zone;
    const inout = payload?.venue?.inout;

    const newPayload = {
      ...payload,
      venue: {
        ...payload.venue,
        status: statusTranslate[status] ?? status,
        crowd_level: crowdLevelTranslate[crowdLevel] ?? crowdLevel,
        region_zone: regionZoneTranslate[regionZone] ?? regionZone,
        inout: inOutTranslate[inout] ?? inout,
      },
    };

    setSelectedVenue(newPayload);
    setDetailOpen(true);
  }, []);

  const updateStatusUpdatingIds = useCallback((venueId, isUpdating) => {
    if (!venueId) return;
    setStatusUpdatingIds((prev) => {
      const next = new Set(prev);
      if (isUpdating) {
        next.add(venueId);
      } else {
        next.delete(venueId);
      }
      return next;
    });
  }, []);

  const handleDisableVenue = useCallback(
    async (payload) => {
      if (!email) {
        setStatusError(t("venuesPage.messages.emailRequired"));
        return;
      }
      const venue = payload?.venue || payload;
      if (!venue?.id) {
        setStatusError(t("venuesPage.messages.updateNeedId"));
        return;
      }
      if (venue.status === "inactive") return;

      updateStatusUpdatingIds(venue.id, true);
      setStatusError("");
      setStatusSuccess("");
      try {
        const body = {
          email,
          venue_id: venue.id,
          status: "inactive",
        };
        await fetchJson(buildApiUrl(backendBaseUrl, VENUE_UPDATE_PATH), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });

        const mergedVenue = { ...venue, status: "inactive" };
        setVenues((prev) =>
          prev.map((item) => (item.venue?.id === venue.id ? { ...item, venue: mergedVenue } : item))
        );
        if (selectedVenue?.venue?.id === venue.id) {
          setSelectedVenue((prev) => (prev ? { ...prev, venue: mergedVenue } : prev));
        }
        setStatusSuccess(t("venuesPage.messages.disableSuccess"));
      } catch (err) {
        console.error(err);
        setStatusError(t("venuesPage.messages.disableFailed"));
      } finally {
        updateStatusUpdatingIds(venue.id, false);
      }
    },
    [backendBaseUrl, email, selectedVenue, t, updateStatusUpdatingIds]
  );

  const buildUpdateFormValues = useCallback((venue = {}) => {
    const domestic =
      venue.is_domestic === true ? "true" : venue.is_domestic === false ? "false" : "";
    return {
      status: venue.status ?? "",
      name: venue.name ?? "",
      full_address: venue.full_address ?? "",
      country_code: venue.country_code ?? "",
      zip_code: venue.zip_code ?? "",
      city: venue.city ?? "",
      district: venue.district ?? "",
      street: venue.street ?? "",
      floor: venue.floor ?? "",
      region_zone: venue.region_zone ?? "",
      is_domestic: domestic,
      place_type: venue.place_type ?? "",
      crowd_level: venue.crowd_level ?? "",
      inout: venue.inout ?? "",
      // latitude: venue.latitude ?? "",
      // longitude: venue.longitude ?? "",
      nearby_landmarks: venue.nearby_landmarks ?? "",
      target_audience: venue.target_audience ?? "",
      venue_type: venue.venue_type ?? "",
      venue_start_time: formatDateInputValue(venue.venue_start_time ?? ""),
      venue_end_time: formatDateInputValue(venue.venue_end_time ?? ""),
      machine_types: venue.machine_types ?? "",
      store_code: venue.store_code ?? "",
      sales_warehouse_name: venue.sales_warehouse_name ?? "",
      external_store_code: venue.external_store_code ?? "",
      external_system_type: venue.external_system_type ?? "",
      external_description: venue.external_description ?? "",
    };
  }, []);

  const handleOpenUpdate = useCallback(
    (payload) => {
      const venue = payload.venue || payload;
      const values = buildUpdateFormValues(venue);
      setUpdateTarget(venue);
      setUpdateForm(values);
      setUpdateOriginal(values);
      setUpdateError("");
      setUpdateSuccess("");
      setUpdateOpen(true);
    },
    [buildUpdateFormValues]
  );

  const handleOpenMachineView = useCallback(() => {
    setViewMode("machines");
    loadMachines();
  }, [loadMachines]);

  const handleBackToVenues = useCallback(() => {
    setViewMode("venues");
    setMachineVenueOpen(false);
    setMachineVenueTarget(null);
    setMachineVenueError("");
  }, []);

  const handleOpenMachineVenue = useCallback((machine) => {
    setMachineVenueTarget(machine);
    setMachineVenueId(machine?.current_venue_id || "");
    setMachineVenueError("");
    setMachineVenueOpen(true);
  }, []);

  const handleUpdateMachineVenue = useCallback(async () => {
    const machineUpdateId = String(getMachineUpdateId(machineVenueTarget) || "");
    if (!machineUpdateId) {
      setMachineVenueError("缺少機台 ID，無法更新場域");
      return;
    }
    if (!machineVenueId) {
      setMachineVenueError("請選擇場域");
      return;
    }

    setMachineVenueSubmitting(true);
    setMachineVenueError("");
    try {
      const json = await fetchJson(buildApiUrl(backendBaseUrl, MACHINE_VENUE_UPDATE_PATH), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Mdm-Auth": API_KEY,
        },
        body: JSON.stringify({
          venue_id: machineVenueId,
          id: [String(machineUpdateId)],
        }),
      });
      if (json?.code && json.code !== 200) {
        throw new Error(json.message || "更新機台場域失敗");
      }

      const selectedVenueOption = activeVenueOptions.find(
        (option) => option.value === machineVenueId
      );
      setMachineRows((prev) =>
        prev.map((machine) =>
          String(getMachineUpdateId(machine) || "") === machineUpdateId
            ? {
                ...machine,
                current_venue_id: machineVenueId,
                current_venue_name: selectedVenueOption?.venue?.name || machine.current_venue_name,
                current_city: selectedVenueOption?.venue?.city || machine.current_city,
                current_brand_name: selectedVenueOption?.brand?.name || machine.current_brand_name,
              }
            : machine
        )
      );
      setMachineVenueSuccess("機台場域已更新");
      setMachineVenueOpen(false);
      setMachineVenueTarget(null);
      loadMachines();
    } catch (err) {
      console.error(err);
      setMachineVenueError(err.message || "更新機台場域失敗");
    } finally {
      setMachineVenueSubmitting(false);
    }
  }, [activeVenueOptions, backendBaseUrl, loadMachines, machineVenueId, machineVenueTarget]);

  const columns = useMemo(
    () => [
      { Header: t("venuesPage.columns.name"), accessor: "name" },
      { Header: t("venuesPage.columns.fullAddress"), accessor: "full_address" },
      { Header: t("venuesPage.columns.city"), accessor: "city" },
      { Header: t("venuesPage.columns.brand"), accessor: "brand_name" },
      { Header: t("venuesPage.columns.machineCount"), accessor: "machine_count" },
      { Header: t("venuesPage.columns.updatedAt"), accessor: "updated_at" },
      {
        Header: t("venuesPage.columns.actions"),
        accessor: "actions",
        Cell: VenueActionCell,
      },
    ],
    [VenueActionCell, t]
  );

  const exportColumns = useMemo(
    () =>
      columns
        .filter((column) => column.accessor !== "actions")
        .map((column) => ({
          header: column.Header,
          accessor: column.accessor,
        })),
    [columns]
  );

  function VenueActionCell({ row }) {
    return (
      <ActionButtonsCell
        row={row}
        onView={handleOpenDetail}
        onUpdate={handleOpenUpdate}
        onDisable={handleDisableVenue}
        viewLabel={t("venuesPage.actions.view")}
        updateLabel={t("venuesPage.actions.update")}
        disableLabel={t("venuesPage.actions.disable")}
        disabledLabel={t("venuesPage.actions.disabled")}
        updatingIds={statusUpdatingIds}
      />
    );
  }

  VenueActionCell.propTypes = {
    row: PropTypes.shape({
      original: PropTypes.object.isRequired,
    }).isRequired,
  };

  function MachineVenueActionCell({ row }) {
    const machine = row.original.raw || row.original;
    const disabled = machine.status !== "啟用";
    return (
      <Button
        variant="outlined"
        size="small"
        sx={outlineButtonSx}
        onClick={() => handleOpenMachineVenue(machine)}
        disabled={disabled}
      >
        編輯場域
      </Button>
    );
  }

  MachineVenueActionCell.propTypes = {
    row: PropTypes.shape({
      original: PropTypes.object.isRequired,
    }).isRequired,
  };

  const machineVenueRows = useMemo(
    () =>
      machineRows.map((machine) => ({
        device_id: machine.device_id || "",
        display_name: machine.display_name || machine.machine_code || machine.device_id || "",
        current_venue_name: machine.current_venue_name || "",
        current_city: machine.current_city || "",
        current_brand_name: machine.current_brand_name || "",
        current_updated_at: machine.current_updated_at || "",
        is_online: machine.is_online ? "上線" : "離線",
        can_operate: machine.can_operate === false ? "不可操作" : "可操作",
        disabled_reason: machine.disabled_reason || "",
        raw: machine,
      })),
    [machineRows]
  );

  const machineVenueColumns = useMemo(
    () => [
      { Header: "機台ID", accessor: "device_id" },
      { Header: "顯示名稱", accessor: "display_name" },
      { Header: "所屬場域", accessor: "current_venue_name" },
      { Header: "城市", accessor: "current_city" },
      { Header: "品牌", accessor: "current_brand_name" },
      {
        Header: "操作",
        accessor: "actions",
        Cell: MachineVenueActionCell,
      },
      { Header: "更新時間", accessor: "current_updated_at" },
    ],
    [MachineVenueActionCell]
  );

  const venueDetail = selectedVenue?.venue || {};
  const brandDetail = selectedVenue?.brand || {};

  const detailMachineRows = useMemo(() => {
    const machines = Array.isArray(selectedVenue?.machines) ? selectedVenue.machines : [];
    return machines.map((machine) => ({
      id: machine.id || "",
      ref: machine.ref || "",
      name: machine.name || "",
      machine_code: machine.machine_code || "",
      machine_name: machine.machine_name || "",
      note_private: machine.note_private || "",
    }));
  }, [selectedVenue]);

  const machineColumns = useMemo(
    () => [
      { Header: t("venuesPage.machineColumns.id"), accessor: "id" },
      { Header: t("venuesPage.machineColumns.ref"), accessor: "ref" },
      { Header: t("venuesPage.machineColumns.name"), accessor: "name" },
      { Header: t("venuesPage.machineColumns.machineCode"), accessor: "machine_code" },
      { Header: t("venuesPage.machineColumns.machineName"), accessor: "machine_name" },
      { Header: t("venuesPage.machineColumns.note"), accessor: "note_private" },
    ],
    [t]
  );

  const handleDownload = useCallback(() => {
    try {
      const headers = exportColumns.map((column) => column.header);
      const bodyRows = venueRows.map((row) =>
        exportColumns.map((column) => {
          const value = row?.[column.accessor];
          if (value === null || value === undefined) return "";
          if (typeof value === "object") return JSON.stringify(value);
          return value;
        })
      );

      const worksheet = XLSX.utils.aoa_to_sheet([headers, ...bodyRows]);

      if (downloadFormat === "xlsx") {
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Venues");
        XLSX.writeFile(workbook, "venues.xlsx");
      } else {
        const csv = `\uFEFF${XLSX.utils.sheet_to_csv(worksheet)}`;
        downloadBlob(new Blob([csv], { type: "text/csv;charset=utf-8;" }), "venues.csv");
      }

      setDownloadDialogOpen(false);
    } catch (err) {
      console.error(err);
      setError(t("venuesPage.messages.loadFailed"));
    }
  }, [downloadFormat, exportColumns, t, venueRows]);

  const handleOpenCreate = useCallback(() => {
    setCreateForm({
      brand_id: "",
      name: "",
      full_address: "",
      country_code: "TW",
      zip_code: "",
      city: "",
      district: "",
      street: "",
      floor: "",
      region_zone: "north",
      is_domestic: true,
      place_type: "",
      crowd_level: "",
      inout: "",
      // latitude: "",
      // longitude: "",
      nearby_landmarks: "",
      target_audience: "",
      venue_type: "",
      venue_start_time: "",
      venue_end_time: "",
      external_store_code: "",
      external_system_type: "",
      external_description: "",
      sales_warehouse_name: "",
    });
    setCreateError("");
    setCreateSuccess("");
    setCreateOpen(true);
  }, []);

  const handleCreateChange = useCallback((key, value) => {
    setCreateForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleCreateVenue = useCallback(async () => {
    if (!createForm.brand_id || !createForm.name || !createForm.full_address) {
      setCreateError(t("venuesPage.messages.requiredFields"));
      return;
    }
    setCreating(true);
    setCreateError("");
    try {
      const payload = {
        brand_id: Number(createForm.brand_id),
        name: createForm.name.trim(),
        full_address: createForm.full_address.trim(),
      };
      const appendField = (key, value) => {
        if (value === undefined || value === null || value === "") return;
        payload[key] = value;
      };
      appendField("country_code", createForm.country_code.trim());
      appendField("zip_code", createForm.zip_code.trim());
      appendField("city", createForm.city.trim());
      appendField("district", createForm.district.trim());
      appendField("street", createForm.street.trim());
      appendField("floor", createForm.floor.trim());
      appendField("region_zone", createForm.region_zone);
      appendField("is_domestic", Boolean(createForm.is_domestic));
      appendField("place_type", createForm.place_type.trim());
      appendField("crowd_level", createForm.crowd_level);
      appendField("inout", createForm.inout.trim());
      // if (createForm.latitude !== "") {
      //   const lat = Number(createForm.latitude);
      //   if (Number.isFinite(lat)) payload.latitude = lat;
      // }
      // if (createForm.longitude !== "") {
      //   const lng = Number(createForm.longitude);
      //   if (Number.isFinite(lng)) payload.longitude = lng;
      // }
      appendField("nearby_landmarks", createForm.nearby_landmarks.trim());
      appendField("target_audience", createForm.target_audience.trim());
      appendField("venue_type", createForm.venue_type.trim());
      appendField("venue_start_time", formatDateToRFC3339(createForm.venue_start_time, false));
      appendField("venue_end_time", formatDateToRFC3339(createForm.venue_end_time, true));
      appendField("external_store_code", createForm.external_store_code.trim());
      appendField("external_system_type", createForm.external_system_type.trim());
      appendField("external_description", createForm.external_description.trim());
      appendField("sales_warehouse_name", createForm.sales_warehouse_name.trim());

      await fetchJson(buildApiUrl(backendBaseUrl, VENUE_CREATE_PATH), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      setCreateSuccess(t("venuesPage.messages.createSuccess"));
      setCreateOpen(false);
      await loadVenues();
    } catch (err) {
      console.error(err);
      setCreateError(t("venuesPage.messages.createFailed"));
    } finally {
      setCreating(false);
    }
  }, [backendBaseUrl, createForm, loadVenues, t]);

  const handleUpdateChange = useCallback(
    (key, value) => {
      setUpdateForm((prev) => ({ ...prev, [key]: value }));
      if (updateSuccess) setUpdateSuccess("");
    },
    [updateSuccess]
  );

  const buildUpdatePayload = useCallback((current, original) => {
    const payload = {};
    const addIfChanged = (key, normalize) => {
      const currentValue = current[key] ?? "";
      const originalValue = original[key] ?? "";
      if (currentValue === originalValue) return;
      const normalized = normalize ? normalize(currentValue) : currentValue;
      payload[key] = normalized;
    };

    addIfChanged("status", (value) => String(value).trim());
    addIfChanged("name", (value) => String(value).trim());
    addIfChanged("full_address", (value) => String(value).trim());
    addIfChanged("country_code", (value) => String(value).trim());
    addIfChanged("zip_code", (value) => String(value).trim());
    addIfChanged("city", (value) => String(value).trim());
    addIfChanged("district", (value) => String(value).trim());
    addIfChanged("street", (value) => String(value).trim());
    addIfChanged("floor", (value) => String(value).trim());
    addIfChanged("region_zone", (value) => String(value).trim());
    addIfChanged("place_type", (value) => String(value).trim());
    addIfChanged("crowd_level", (value) => String(value).trim());
    addIfChanged("inout", (value) => String(value).trim());
    addIfChanged("nearby_landmarks", (value) => String(value).trim());
    addIfChanged("target_audience", (value) => String(value).trim());
    addIfChanged("venue_type", (value) => String(value).trim());
    addIfChanged("venue_start_time", (value) => formatDateToRFC3339(String(value).trim(), false));
    addIfChanged("venue_end_time", (value) => formatDateToRFC3339(String(value).trim(), true));
    addIfChanged("machine_types", (value) => String(value).trim());
    addIfChanged("store_code", (value) => String(value).trim());
    addIfChanged("sales_warehouse_name", (value) => String(value).trim());
    addIfChanged("external_store_code", (value) => String(value).trim());
    addIfChanged("external_system_type", (value) => String(value).trim());
    addIfChanged("external_description", (value) => String(value).trim());

    if ((current.is_domestic ?? "") !== (original.is_domestic ?? "")) {
      if (current.is_domestic === "true") payload.is_domestic = true;
      if (current.is_domestic === "false") payload.is_domestic = false;
    }

    // if ((current.latitude ?? "") !== (original.latitude ?? "")) {
    //   if (current.latitude !== "") {
    //     const lat = Number(current.latitude);
    //     if (Number.isFinite(lat)) payload.latitude = lat;
    //   }
    // }
    // if ((current.longitude ?? "") !== (original.longitude ?? "")) {
    //   if (current.longitude !== "") {
    //     const lng = Number(current.longitude);
    //     if (Number.isFinite(lng)) payload.longitude = lng;
    //   }
    // }

    return payload;
  }, []);

  const handleUpdateVenue = useCallback(async () => {
    if (!email) {
      setUpdateError(t("venuesPage.messages.emailRequired"));
      return;
    }
    if (!updateTarget?.id) {
      setUpdateError(t("venuesPage.messages.updateNeedId"));
      return;
    }
    const payload = buildUpdatePayload(updateForm, updateOriginal);
    if (!Object.keys(payload).length) {
      setUpdateError(t("venuesPage.messages.updateNoChanges"));
      return;
    }
    setUpdating(true);
    setUpdateError("");
    try {
      const body = {
        email,
        venue_id: updateTarget.id,
        ...payload,
      };
      await fetchJson(buildApiUrl(backendBaseUrl, VENUE_UPDATE_PATH), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const mergedVenue = { ...updateTarget, ...payload };
      setVenues((prev) =>
        prev.map((item) =>
          item.venue?.id === updateTarget.id ? { ...item, venue: mergedVenue } : item
        )
      );
      if (selectedVenue?.venue?.id === updateTarget.id) {
        setSelectedVenue((prev) => (prev ? { ...prev, venue: mergedVenue } : prev));
      }
      setUpdateSuccess(t("venuesPage.messages.updateSuccess"));
      setUpdateOpen(false);
    } catch (err) {
      console.error(err);
      setUpdateError(t("venuesPage.messages.updateFailed"));
    } finally {
      setUpdating(false);
    }
  }, [
    backendBaseUrl,
    buildUpdatePayload,
    email,
    selectedVenue,
    t,
    updateForm,
    updateOriginal,
    updateTarget,
  ]);

  const venueFields = useMemo(
    () => [
      // { key: "id", label: t("venuesPage.fields.id") },
      { key: "status", label: t("venuesPage.fields.status") },
      // { key: "brand_id", label: t("venuesPage.fields.brandId") },
      { key: "name", label: t("venuesPage.fields.name") },
      { key: "full_address", label: t("venuesPage.fields.fullAddress") },
      { key: "country_code", label: t("venuesPage.fields.countryCode") },
      { key: "zip_code", label: t("venuesPage.fields.zipCode") },
      { key: "city", label: t("venuesPage.fields.city") },
      { key: "district", label: t("venuesPage.fields.district") },
      { key: "street", label: t("venuesPage.fields.street") },
      { key: "floor", label: t("venuesPage.fields.floor") },
      { key: "region_zone", label: t("venuesPage.fields.regionZone") },
      { key: "is_domestic", label: t("venuesPage.fields.isDomestic") },
      { key: "place_type", label: t("venuesPage.fields.placeType") },
      { key: "crowd_level", label: t("venuesPage.fields.crowdLevel") },
      { key: "inout", label: t("venuesPage.fields.inout") },
      // { key: "latitude", label: t("venuesPage.fields.latitude") },
      // { key: "longitude", label: t("venuesPage.fields.longitude") },
      { key: "nearby_landmarks", label: t("venuesPage.fields.nearbyLandmarks") },
      { key: "target_audience", label: t("venuesPage.fields.targetAudience") },
      { key: "venue_type", label: t("venuesPage.fields.venueType") },
      { key: "venue_start_time", label: t("venuesPage.fields.venueStartTime") },
      { key: "venue_end_time", label: t("venuesPage.fields.venueEndTime") },
      { key: "machine_types", label: t("venuesPage.fields.machineTypes") },
      { key: "store_code", label: t("venuesPage.fields.storeCode") },
      { key: "sales_warehouse_name", label: t("venuesPage.fields.salesWarehouseName") },
      { key: "external_store_code", label: t("venuesPage.fields.externalStoreCode") },
      { key: "external_system_type", label: t("venuesPage.fields.externalSystemType") },
      { key: "external_description", label: t("venuesPage.fields.externalDescription") },
      { key: "created_at", label: t("venuesPage.fields.createdAt") },
      { key: "updated_at", label: t("venuesPage.fields.updatedAt") },
    ],
    [t]
  );

  const brandFields = useMemo(
    () => [
      // { key: "id", label: t("venuesPage.brandFields.id") },
      // { key: "ref", label: t("venuesPage.brandFields.ref") },
      { key: "name", label: t("venuesPage.brandFields.name") },
      { key: "name_alias", label: t("venuesPage.brandFields.nameAlias") },
      { key: "status", label: t("venuesPage.brandFields.status") },
      { key: "address", label: t("venuesPage.brandFields.address") },
      { key: "zip", label: t("venuesPage.brandFields.zip") },
      { key: "town", label: t("venuesPage.brandFields.town") },
      { key: "country_code", label: t("venuesPage.brandFields.countryCode") },
      { key: "phone", label: t("venuesPage.brandFields.phone") },
      {
        key: "email",
        label: t("venuesPage.brandFields.email"),
      },
      { key: "note_private", label: t("venuesPage.brandFields.notePrivate") },
      { key: "machine_descendants", label: t("venuesPage.brandFields.machineDescendants") },
    ],
    [t]
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <MDTypography variant="h6" color="white">
                  <RoutePageTitle />
                </MDTypography>
                <MDBox display="flex" gap={2}>
                  {viewMode === "venues" ? (
                    <>
                      <Button
                        id="download"
                        variant="outlined"
                        sx={headerButtonSx}
                        onClick={() => {
                          setDownloadFormat("csv");
                          setDownloadDialogOpen(true);
                        }}
                      >
                        匯出表格
                      </Button>

                      <Button variant="outlined" sx={headerButtonSx} onClick={handleOpenCreate}>
                        {t("venuesPage.actions.create")}
                      </Button>
                    </>
                  ) : (
                    <Button variant="outlined" sx={headerButtonSx} onClick={handleBackToVenues}>
                      返回場域頁面
                    </Button>
                  )}
                </MDBox>
              </MDBox>
              <MDBox pt={3} px={3} pb={3}>
                {viewMode === "venues" ? (
                  <>
                    <MDBox display="flex" justifyContent="flex-end" mb={2}>
                      <Button
                        variant="contained"
                        onClick={handleOpenMachineView}
                        sx={{
                          borderRadius: "10px",
                          backgroundColor: "#ef9a2a",
                          color: "#FFFFFF",
                          fontWeight: 700,
                          boxShadow: "none",
                          "&:hover": {
                            backgroundColor: "#d98216",
                            boxShadow: "none",
                          },
                        }}
                      >
                        檢視機台與場域資訊
                      </Button>
                    </MDBox>
                    {loading && (
                      <MDBox display="flex" alignItems="center" gap={1} mb={2}>
                        <CircularProgress size={20} />
                        <MDTypography variant="body2">
                          {t("venuesPage.messages.loading")}
                        </MDTypography>
                      </MDBox>
                    )}
                    {error && (
                      <MDTypography variant="body2" color="error" mb={2}>
                        {error}
                      </MDTypography>
                    )}
                    <DataTable
                      table={{ columns, rows: venueRows }}
                      entriesPerPage={{ defaultValue: 10, entries: [10, 25, 50] }}
                      canSearch
                      showTotalEntries={false}
                      isSorted
                      noEndBorder
                    />
                  </>
                ) : (
                  <>
                    {machineLoading && (
                      <MDBox display="flex" alignItems="center" gap={1} mb={2}>
                        <CircularProgress size={20} />
                        <MDTypography variant="body2">載入機台資料中...</MDTypography>
                      </MDBox>
                    )}
                    {machineError && (
                      <MDTypography variant="body2" color="error" mb={2}>
                        {machineError}
                      </MDTypography>
                    )}
                    <DataTable
                      table={{ columns: machineVenueColumns, rows: machineVenueRows }}
                      entriesPerPage={{ defaultValue: 10, entries: [10, 25, 50] }}
                      canSearch
                      showTotalEntries={false}
                      isSorted
                      noEndBorder
                    />
                  </>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Dialog
        open={downloadDialogOpen}
        onClose={() => setDownloadDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>匯出表格</DialogTitle>
        <DialogContent dividers>
          <FormControl component="fieldset" fullWidth>
            <RadioGroup
              value={downloadFormat}
              onChange={(event) => setDownloadFormat(event.target.value)}
            >
              <FormControlLabel value="csv" control={<Radio />} label="CSV" />
              <FormControlLabel value="xlsx" control={<Radio />} label="XLSX" />
            </RadioGroup>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => setDownloadDialogOpen(false)}
            sx={{
              borderRadius: "10px",
              borderColor: "#1A73E8",
              color: "#1A73E8",
              fontWeight: 600,
              "&:hover": {
                borderColor: "#1557B0",
                backgroundColor: "rgba(26, 115, 232, 0.08)",
              },
            }}
          >
            取消
          </Button>
          <Button
            variant="contained"
            onClick={handleDownload}
            sx={{
              borderRadius: "10px",
              backgroundColor: "#1A73E8",
              color: "#FFFFFF",
              fontWeight: 600,
              "&:hover": {
                backgroundColor: "#1557B0",
              },
            }}
          >
            確認下載
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>{t("venuesPage.dialogs.detailTitle")}</DialogTitle>
        <DialogContent dividers>
          <MDBox mb={3}>
            <MDTypography variant="subtitle2" gutterBottom>
              {t("venuesPage.sections.venue")}
            </MDTypography>
            <Grid container spacing={2}>
              {venueFields.map((field) => (
                <Grid item xs={12} md={6} key={field.key}>
                  <TextField
                    label={field.label}
                    value={formatValue(
                      venueDetail[field.key],
                      t("venuesPage.values.yes"),
                      t("venuesPage.values.no")
                    )}
                    fullWidth
                    size="small"
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
              ))}
            </Grid>
          </MDBox>

          <MDBox mb={3}>
            <MDTypography variant="subtitle2" gutterBottom>
              {t("venuesPage.sections.brand")}
            </MDTypography>
            <Grid container spacing={2}>
              {brandFields.map((field) => {
                if (field.key === "email") {
                  return (
                    <Grid item xs={12} md={6} key={field.key}>
                      <TextField
                        label={field.label}
                        value={formatValue(
                          brandDetail?.array_options?.options_contact_information,
                          t("venuesPage.values.yes"),
                          t("venuesPage.values.no")
                        )}
                        fullWidth
                        size="small"
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>
                  );
                }
                return (
                  <Grid item xs={12} md={6} key={field.key}>
                    <TextField
                      label={field.label}
                      value={formatValue(
                        brandDetail[field.key],
                        t("venuesPage.values.yes"),
                        t("venuesPage.values.no")
                      )}
                      fullWidth
                      size="small"
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </MDBox>

          <MDBox>
            <MDTypography variant="subtitle2" gutterBottom>
              {t("venuesPage.sections.machines")}
            </MDTypography>
            <DataTable
              table={{ columns: machineColumns, rows: detailMachineRows }}
              entriesPerPage={{ defaultValue: 5, entries: [5, 10] }}
              canSearch
              showTotalEntries={false}
              isSorted
              noEndBorder
            />
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" sx={outlineButtonSx} onClick={() => setDetailOpen(false)}>
            {t("venuesPage.actions.close")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{t("venuesPage.dialogs.createTitle")}</DialogTitle>
        <DialogContent dividers>
          {createError && (
            <MDTypography variant="body2" color="error" mb={2}>
              {createError}
            </MDTypography>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small" sx={selectSx}>
                <InputLabel>{t("venuesPage.form.brand")}</InputLabel>
                <Select
                  value={createForm.brand_id}
                  label={t("venuesPage.form.brand")}
                  onChange={(event) => handleCreateChange("brand_id", event.target.value)}
                >
                  {!brands.length && <MenuItem value="">{t("venuesPage.values.unset")}</MenuItem>}
                  {brands.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label={t("venuesPage.form.name")}
                value={createForm.name}
                onChange={(event) => handleCreateChange("name", event.target.value)}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t("venuesPage.form.fullAddress")}
                value={createForm.full_address}
                onChange={(event) => handleCreateChange("full_address", event.target.value)}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label={t("venuesPage.form.countryCode")}
                value={createForm.country_code}
                onChange={(event) => handleCreateChange("country_code", event.target.value)}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label={t("venuesPage.form.zipCode")}
                value={createForm.zip_code}
                onChange={(event) => handleCreateChange("zip_code", event.target.value)}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label={t("venuesPage.form.city")}
                value={createForm.city}
                onChange={(event) => handleCreateChange("city", event.target.value)}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label={t("venuesPage.form.district")}
                value={createForm.district}
                onChange={(event) => handleCreateChange("district", event.target.value)}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label={t("venuesPage.form.street")}
                value={createForm.street}
                onChange={(event) => handleCreateChange("street", event.target.value)}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label={t("venuesPage.form.floor")}
                value={createForm.floor}
                onChange={(event) => handleCreateChange("floor", event.target.value)}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small" sx={selectSx}>
                <InputLabel>{t("venuesPage.form.regionZone")}</InputLabel>
                <Select
                  value={createForm.region_zone}
                  label={t("venuesPage.form.regionZone")}
                  onChange={(event) => handleCreateChange("region_zone", event.target.value)}
                >
                  <MenuItem value="north">{t("venuesPage.region.north")}</MenuItem>
                  <MenuItem value="central">{t("venuesPage.region.central")}</MenuItem>
                  <MenuItem value="south">{t("venuesPage.region.south")}</MenuItem>
                  <MenuItem value="east">{t("venuesPage.region.east")}</MenuItem>
                  <MenuItem value="overseas">{t("venuesPage.region.overseas")}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small" sx={selectSx}>
                <InputLabel>{t("venuesPage.form.crowdLevel")}</InputLabel>
                <Select
                  value={createForm.crowd_level}
                  label={t("venuesPage.form.crowdLevel")}
                  onChange={(event) => handleCreateChange("crowd_level", event.target.value)}
                >
                  <MenuItem value="">{t("venuesPage.values.unset")}</MenuItem>
                  <MenuItem value="low">{t("venuesPage.crowd.low")}</MenuItem>
                  <MenuItem value="medium">{t("venuesPage.crowd.medium")}</MenuItem>
                  <MenuItem value="high">{t("venuesPage.crowd.high")}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small" sx={selectSx}>
                <InputLabel>{t("venuesPage.form.inout")}</InputLabel>
                <Select
                  value={createForm.inout}
                  label={t("venuesPage.form.inout")}
                  onChange={(event) => handleCreateChange("inout", event.target.value)}
                >
                  <MenuItem value="">{t("venuesPage.values.unset")}</MenuItem>
                  <MenuItem value="indoor">{t("venuesPage.inout.indoor")}</MenuItem>
                  <MenuItem value="outdoor">{t("venuesPage.inout.outdoor")}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label={t("venuesPage.form.placeType")}
                value={createForm.place_type}
                onChange={(event) => handleCreateChange("place_type", event.target.value)}
                fullWidth
                size="small"
              />
            </Grid>
            {/* <Grid item xs={12} md={4}>
              <TextField
                label={t("venuesPage.form.latitude")}
                value={createForm.latitude}
                onChange={(event) => handleCreateChange("latitude", event.target.value)}
                fullWidth
                size="small"
                type="number"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label={t("venuesPage.form.longitude")}
                value={createForm.longitude}
                onChange={(event) => handleCreateChange("longitude", event.target.value)}
                fullWidth
                size="small"
                type="number"
              />
            </Grid> */}
            <Grid item xs={12}>
              <TextField
                label={t("venuesPage.form.nearbyLandmarks")}
                value={createForm.nearby_landmarks}
                onChange={(event) => handleCreateChange("nearby_landmarks", event.target.value)}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t("venuesPage.form.targetAudience")}
                value={createForm.target_audience}
                onChange={(event) => handleCreateChange("target_audience", event.target.value)}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label={t("venuesPage.form.venueType")}
                value={createForm.venue_type}
                onChange={(event) => handleCreateChange("venue_type", event.target.value)}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label={t("venuesPage.form.venueStartTime")}
                value={createForm.venue_start_time}
                onChange={(event) => handleCreateChange("venue_start_time", event.target.value)}
                fullWidth
                size="small"
                type="date"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label={t("venuesPage.form.venueEndTime")}
                value={createForm.venue_end_time}
                onChange={(event) => handleCreateChange("venue_end_time", event.target.value)}
                fullWidth
                size="small"
                type="date"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label={t("venuesPage.form.externalStoreCode")}
                value={createForm.external_store_code}
                onChange={(event) => handleCreateChange("external_store_code", event.target.value)}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label={t("venuesPage.form.externalSystemType")}
                value={createForm.external_system_type}
                onChange={(event) => handleCreateChange("external_system_type", event.target.value)}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label={t("venuesPage.form.salesWarehouseName")}
                value={createForm.sales_warehouse_name}
                onChange={(event) => handleCreateChange("sales_warehouse_name", event.target.value)}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label={t("venuesPage.form.externalDescription")}
                value={createForm.external_description}
                onChange={(event) => handleCreateChange("external_description", event.target.value)}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(createForm.is_domestic)}
                    onChange={(event) => handleCreateChange("is_domestic", event.target.checked)}
                  />
                }
                label={t("venuesPage.form.isDomestic")}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" sx={outlineButtonSx} onClick={() => setCreateOpen(false)}>
            {t("venuesPage.actions.close")}
          </Button>
          <Button
            variant="outlined"
            sx={outlineButtonSx}
            onClick={handleCreateVenue}
            disabled={creating}
          >
            {creating ? t("venuesPage.actions.creating") : t("venuesPage.actions.confirm")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={updateOpen} onClose={() => setUpdateOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{t("venuesPage.dialogs.updateTitle")}</DialogTitle>
        <DialogContent dividers>
          {updateError && (
            <MDTypography variant="body2" color="error" mb={2}>
              {updateError}
            </MDTypography>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small" sx={selectSx}>
                <InputLabel>{t("venuesPage.form.status")}</InputLabel>
                <Select
                  value={updateForm.status ?? ""}
                  label={t("venuesPage.form.status")}
                  onChange={(event) => handleUpdateChange("status", event.target.value)}
                >
                  <MenuItem value="">{t("venuesPage.values.unset")}</MenuItem>
                  <MenuItem value="active">{statusTranslate["active"]}</MenuItem>
                  <MenuItem value="inactive">{statusTranslate["inactive"]}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                label={t("venuesPage.form.name")}
                value={updateForm.name ?? ""}
                onChange={(event) => handleUpdateChange("name", event.target.value)}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t("venuesPage.form.fullAddress")}
                value={updateForm.full_address ?? ""}
                onChange={(event) => handleUpdateChange("full_address", event.target.value)}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label={t("venuesPage.form.countryCode")}
                value={updateForm.country_code ?? ""}
                onChange={(event) => handleUpdateChange("country_code", event.target.value)}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label={t("venuesPage.form.zipCode")}
                value={updateForm.zip_code ?? ""}
                onChange={(event) => handleUpdateChange("zip_code", event.target.value)}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label={t("venuesPage.form.city")}
                value={updateForm.city ?? ""}
                onChange={(event) => handleUpdateChange("city", event.target.value)}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label={t("venuesPage.form.district")}
                value={updateForm.district ?? ""}
                onChange={(event) => handleUpdateChange("district", event.target.value)}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label={t("venuesPage.form.street")}
                value={updateForm.street ?? ""}
                onChange={(event) => handleUpdateChange("street", event.target.value)}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label={t("venuesPage.form.floor")}
                value={updateForm.floor ?? ""}
                onChange={(event) => handleUpdateChange("floor", event.target.value)}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small" sx={selectSx}>
                <InputLabel>{t("venuesPage.form.regionZone")}</InputLabel>
                <Select
                  value={updateForm.region_zone ?? ""}
                  label={t("venuesPage.form.regionZone")}
                  onChange={(event) => handleUpdateChange("region_zone", event.target.value)}
                >
                  <MenuItem value="">{t("venuesPage.values.unset")}</MenuItem>
                  <MenuItem value="north">{t("venuesPage.region.north")}</MenuItem>
                  <MenuItem value="central">{t("venuesPage.region.central")}</MenuItem>
                  <MenuItem value="south">{t("venuesPage.region.south")}</MenuItem>
                  <MenuItem value="east">{t("venuesPage.region.east")}</MenuItem>
                  <MenuItem value="overseas">{t("venuesPage.region.overseas")}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small" sx={selectSx}>
                <InputLabel>{t("venuesPage.form.crowdLevel")}</InputLabel>
                <Select
                  value={updateForm.crowd_level ?? ""}
                  label={t("venuesPage.form.crowdLevel")}
                  onChange={(event) => handleUpdateChange("crowd_level", event.target.value)}
                >
                  <MenuItem value="">{t("venuesPage.values.unset")}</MenuItem>
                  <MenuItem value="low">{t("venuesPage.crowd.low")}</MenuItem>
                  <MenuItem value="medium">{t("venuesPage.crowd.medium")}</MenuItem>
                  <MenuItem value="high">{t("venuesPage.crowd.high")}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small" sx={selectSx}>
                <InputLabel>{t("venuesPage.form.inout")}</InputLabel>
                <Select
                  value={updateForm.inout ?? ""}
                  label={t("venuesPage.form.inout")}
                  onChange={(event) => handleUpdateChange("inout", event.target.value)}
                >
                  <MenuItem value="">{t("venuesPage.values.unset")}</MenuItem>
                  <MenuItem value="indoor">{t("venuesPage.inout.indoor")}</MenuItem>
                  <MenuItem value="outdoor">{t("venuesPage.inout.outdoor")}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small" sx={selectSx}>
                <InputLabel>{t("venuesPage.form.isDomestic")}</InputLabel>
                <Select
                  value={updateForm.is_domestic ?? ""}
                  label={t("venuesPage.form.isDomestic")}
                  onChange={(event) => handleUpdateChange("is_domestic", event.target.value)}
                >
                  <MenuItem value="">{t("venuesPage.values.unset")}</MenuItem>
                  <MenuItem value="true">{t("venuesPage.values.yes")}</MenuItem>
                  <MenuItem value="false">{t("venuesPage.values.no")}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label={t("venuesPage.form.placeType")}
                value={updateForm.place_type ?? ""}
                onChange={(event) => handleUpdateChange("place_type", event.target.value)}
                fullWidth
                size="small"
              />
            </Grid>
            {/* <Grid item xs={12} md={4}>
              <TextField
                label={t("venuesPage.form.latitude")}
                value={updateForm.latitude ?? ""}
                onChange={(event) => handleUpdateChange("latitude", event.target.value)}
                fullWidth
                size="small"
                type="number"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label={t("venuesPage.form.longitude")}
                value={updateForm.longitude ?? ""}
                onChange={(event) => handleUpdateChange("longitude", event.target.value)}
                fullWidth
                size="small"
                type="number"
              />
            </Grid> */}
            <Grid item xs={12}>
              <TextField
                label={t("venuesPage.form.nearbyLandmarks")}
                value={updateForm.nearby_landmarks ?? ""}
                onChange={(event) => handleUpdateChange("nearby_landmarks", event.target.value)}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t("venuesPage.form.targetAudience")}
                value={updateForm.target_audience ?? ""}
                onChange={(event) => handleUpdateChange("target_audience", event.target.value)}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label={t("venuesPage.form.venueType")}
                value={updateForm.venue_type ?? ""}
                onChange={(event) => handleUpdateChange("venue_type", event.target.value)}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label={t("venuesPage.form.venueStartTime")}
                value={updateForm.venue_start_time ?? ""}
                onChange={(event) => handleUpdateChange("venue_start_time", event.target.value)}
                fullWidth
                size="small"
                type="date"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label={t("venuesPage.form.venueEndTime")}
                value={updateForm.venue_end_time ?? ""}
                onChange={(event) => handleUpdateChange("venue_end_time", event.target.value)}
                fullWidth
                size="small"
                type="date"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t("venuesPage.form.machineTypes")}
                value={updateForm.machine_types ?? ""}
                onChange={(event) => handleUpdateChange("machine_types", event.target.value)}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label={t("venuesPage.form.storeCode")}
                value={updateForm.store_code ?? ""}
                onChange={(event) => handleUpdateChange("store_code", event.target.value)}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label={t("venuesPage.form.salesWarehouseName")}
                value={updateForm.sales_warehouse_name ?? ""}
                onChange={(event) => handleUpdateChange("sales_warehouse_name", event.target.value)}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label={t("venuesPage.form.externalStoreCode")}
                value={updateForm.external_store_code ?? ""}
                onChange={(event) => handleUpdateChange("external_store_code", event.target.value)}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label={t("venuesPage.form.externalSystemType")}
                value={updateForm.external_system_type ?? ""}
                onChange={(event) => handleUpdateChange("external_system_type", event.target.value)}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label={t("venuesPage.form.externalDescription")}
                value={updateForm.external_description ?? ""}
                onChange={(event) => handleUpdateChange("external_description", event.target.value)}
                fullWidth
                size="small"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" sx={outlineButtonSx} onClick={() => setUpdateOpen(false)}>
            {t("venuesPage.actions.close")}
          </Button>
          <Button
            variant="outlined"
            sx={outlineButtonSx}
            onClick={handleUpdateVenue}
            disabled={updating}
          >
            {updating ? t("venuesPage.actions.updating") : t("venuesPage.actions.confirm")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={machineVenueOpen}
        onClose={() => setMachineVenueOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>編輯機台場域</DialogTitle>
        <DialogContent dividers>
          {machineVenueError && (
            <MDTypography variant="body2" color="error" mb={2}>
              {machineVenueError}
            </MDTypography>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="機台 ID"
                value={machineVenueTarget?.device_id || "-"}
                fullWidth
                size="small"
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <MDTypography variant="h6" mb={1}>
                機台場域變更
              </MDTypography>
              <FormControl fullWidth size="small" sx={selectSx}>
                <InputLabel>選擇場域</InputLabel>
                <Select
                  value={machineVenueId}
                  label="選擇場域"
                  onChange={(event) => {
                    setMachineVenueId(event.target.value);
                    if (machineVenueError) setMachineVenueError("");
                  }}
                >
                  {!activeVenueOptions.length && <MenuItem value="">無可選場域</MenuItem>}
                  {activeVenueOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            sx={outlineButtonSx}
            onClick={() => setMachineVenueOpen(false)}
            disabled={machineVenueSubmitting}
          >
            關閉
          </Button>
          <Button
            variant="contained"
            onClick={handleUpdateMachineVenue}
            disabled={machineVenueSubmitting}
            sx={{
              borderRadius: "10px",
              backgroundColor: "#1A73E8",
              color: "#FFFFFF",
              fontWeight: 600,
              "&:hover": {
                backgroundColor: "#1557B0",
              },
            }}
          >
            {machineVenueSubmitting ? "更新中..." : "確認"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(createSuccess)}
        autoHideDuration={3000}
        onClose={() => setCreateSuccess("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="success" onClose={() => setCreateSuccess("")}>
          {createSuccess}
        </Alert>
      </Snackbar>

      <Snackbar
        open={Boolean(updateSuccess)}
        autoHideDuration={3000}
        onClose={() => setUpdateSuccess("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="success" onClose={() => setUpdateSuccess("")}>
          {updateSuccess}
        </Alert>
      </Snackbar>

      <Snackbar
        open={Boolean(statusSuccess)}
        autoHideDuration={3000}
        onClose={() => setStatusSuccess("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="success" onClose={() => setStatusSuccess("")}>
          {statusSuccess}
        </Alert>
      </Snackbar>

      <Snackbar
        open={Boolean(machineVenueSuccess)}
        autoHideDuration={3000}
        onClose={() => setMachineVenueSuccess("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="success" onClose={() => setMachineVenueSuccess("")}>
          {machineVenueSuccess}
        </Alert>
      </Snackbar>

      <Snackbar
        open={Boolean(statusError)}
        autoHideDuration={4000}
        onClose={() => setStatusError("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="error" onClose={() => setStatusError("")}>
          {statusError}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default VenuesBK;
